import { DIRS, hexDist, hexLen, hexRing, type Hex } from '../hex.ts';
import { imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng, type RngState } from '../math/rng.ts';

/**
 * The wedges, small (docs/sendas/guerrero.md §4, «Las cuñas», chapter 1): the ground of the ledge. A guard house in the
 * middle of a round clearing (the hex 0,0, where the column goes), a low yard wall round it with six gaps, and on one
 * side, under the hanging cornice, a ledge one hex wide that runs along the cliff and comes down to one of the gaps:
 * the strip of three hexes. A column comes in at the clearing's rim and takes the shortest way to the house. Three
 * shields go into three gaps: the right three send the way under the cornice, where it narrows; the obvious three
 * (the gaps right in front of the house) send it round by a side gap, far from the cornice.
 *
 * The generator tries layouts from the seed (where the column comes in, which side the cliff is on, where the gaps and
 * a few loose outcrops are) and keeps one where exactly one way of planting three shields in the six gaps sends every
 * shortest way through the whole strip, none of the other nineteen touches it, and the three front gaps aren't it.
 * Integers only, and nothing but the seed: the server builds the same one.
 */

/** The clearing's radius (hexes): the column comes in at its rim. */
export const WEDGE_RADIUS = 7;
/** The yard wall's ring round the house. */
const WALL = 4;
/** How many gaps the wall has, how many shields there are, how long the strip is. */
export const WEDGE_GAPS = 6;
export const WEDGE_SHIELDS = 3;

export interface WedgeLayout {
  /** Where the column comes in (the clearing's rim). */
  entry: Hex;
  /** Everything that can't be walked: the wall (gaps aside), the cliff and the outcrops. */
  rocks: Hex[];
  /** The wall's stones, the cliff's and the outcrops' (for the drawing). */
  wall: Hex[];
  cliff: Hex[];
  crags: Hex[];
  /** The six gaps, along the wall. */
  gaps: Hex[];
  /** The strip under the cornice, from its mouth's side to the gap it comes down to. */
  strip: Hex[];
  /**
   * The loose rock: it sits on the wall's stone between the yard and the strip's middle, under the cornice's lip
   * (`rock`), and is pushed from the yard's side of it (`nook`): from there the hero can't reach the strip.
   */
  rock: Hex;
  nook: Hex;
  /** The one planting that sends the column under the cornice (gap indices, ascending). */
  solution: number[];
  /** The three gaps nearest the column's way in: the obvious planting, and the wrong one. */
  front: number[];
}

const key = (h: Hex): string => `${h.q},${h.r}`;
const add = (a: Hex, b: Hex): Hex => ({ q: a.q + b.q, r: a.r + b.r });
const nbrs = (h: Hex): Hex[] => DIRS.map((d) => add(h, d));

/** A sixth of a turn (cube rotation) and a mirror, for the layouts' variety. */
const rot = (h: Hex, n: number): Hex => {
  let q = h.q;
  let r = h.r;
  for (let i = 0; i < n; i++) {
    const s = -q - r;
    [q, r] = [-r, -s];
  }
  return { q, r };
};
const mirror = (h: Hex, m: boolean): Hex => (m ? { q: h.q, r: -h.q - h.r } : h);

/** The clearing's hexes by index: (r + R)·(2R + 1) + (q + R), and each one's six neighbours (−1 off the clearing). */
const W = 2 * WEDGE_RADIUS + 1;
export const wedgeCell = (h: Hex): number => (hexLen(h.q, h.r) > WEDGE_RADIUS ? -1 : (h.r + WEDGE_RADIUS) * W + (h.q + WEDGE_RADIUS));
const NB: Int16Array = (() => {
  const nb = new Int16Array(W * W * 6).fill(-1);
  for (let r = -WEDGE_RADIUS; r <= WEDGE_RADIUS; r++)
    for (let q = -WEDGE_RADIUS; q <= WEDGE_RADIUS; q++) {
      const id = wedgeCell({ q, r });
      if (id < 0) continue;
      for (let k = 0; k < 6; k++) nb[id * 6 + k] = wedgeCell(add({ q, r }, DIRS[k] as Hex));
    }
  return nb;
})();

/** Walkable ground for a layout, by cell (1: open): inside the clearing, not rock, not shut by a shield. */
export type WedgeGround = Uint8Array;

/** Steps from `from` to every cell (−1 unreachable). */
export const wedgeDist = (g: WedgeGround, from: number): Int16Array => {
  const d = new Int16Array(W * W).fill(-1);
  if (from < 0 || !g[from]) return d;
  d[from] = 0;
  const queue = new Int16Array(W * W);
  let head = 0;
  let tail = 0;
  queue[tail++] = from;
  while (head < tail) {
    const c = queue[head++] as number;
    const n = (d[c] as number) + 1;
    for (let k = c * 6, e = k + 6; k < e; k++) {
      const x = NB[k] as number;
      if (x < 0 || !g[x] || (d[x] as number) >= 0) continue;
      d[x] = n;
      queue[tail++] = x;
    }
  }
  return d;
};

/**
 * Where the shortest ways from `a` to `b` pass: `on` marks every cell on one, `only` the ones that are the only cell at
 * their distance (every shortest way goes through them: one hex wide). `len` −1 when `b` can't be reached.
 */
export const wedgeChokes = (g: WedgeGround, a: number, b: number): { len: number; on: Uint8Array; only: Uint8Array } => {
  const da = wedgeDist(g, a);
  const db = wedgeDist(g, b);
  const len = b >= 0 ? (da[b] as number) : -1;
  const on = new Uint8Array(W * W);
  const only = new Uint8Array(W * W);
  if (len < 0) return { len: -1, on, only };
  const count = new Int16Array(len + 1);
  const last = new Int16Array(len + 1);
  for (let c = 0; c < W * W; c++) {
    const x = da[c] as number;
    const y = db[c] as number;
    if (x < 0 || y < 0 || x + y !== len) continue;
    on[c] = 1;
    count[x] = (count[x] as number) + 1;
    last[x] = c;
  }
  for (let l = 0; l <= len; l++) if (count[l] === 1) only[last[l] as number] = 1;
  return { len, on, only };
};

/** Every planting of three shields in six gaps (ascending indices), in a fixed order. */
export const WEDGE_PLANTINGS: number[][] = (() => {
  const out: number[][] = [];
  for (let a = 0; a < WEDGE_GAPS; a++) for (let b = a + 1; b < WEDGE_GAPS; b++) for (let c = b + 1; c < WEDGE_GAPS; c++) out.push([a, b, c]);
  return out;
})();

/** The ground of a layout with these gaps shut. */
export const wedgeGround = (L: Pick<WedgeLayout, 'rocks' | 'gaps'>, shut: number[]): WedgeGround => {
  const g = new Uint8Array(W * W);
  for (let r = -WEDGE_RADIUS; r <= WEDGE_RADIUS; r++)
    for (let q = -WEDGE_RADIUS; q <= WEDGE_RADIUS; q++) {
      const id = wedgeCell({ q, r });
      if (id >= 0) g[id] = 1;
    }
  for (const h of L.rocks) {
    const id = wedgeCell(h);
    if (id >= 0) g[id] = 0;
  }
  for (const i of shut) {
    const h = L.gaps[i];
    const id = h ? wedgeCell(h) : -1;
    if (id >= 0) g[id] = 0;
  }
  return g;
};

/**
 * How a planting sends the column: through the whole strip on every shortest way (`under`), past it on none (`clear`),
 * or some of each (`mixed`); null if the house can't be reached.
 */
export const wedgeWay = (L: WedgeLayout, shut: number[]): 'under' | 'clear' | 'mixed' | null => {
  const c = wedgeChokes(wedgeGround(L, shut), wedgeCell(L.entry), wedgeCell({ q: 0, r: 0 }));
  if (c.len < 0) return null;
  const ids = L.strip.map(wedgeCell);
  if (ids.every((i) => c.only[i] === 1)) return 'under';
  if (ids.every((i) => c.on[i] === 0)) return 'clear';
  return 'mixed';
};

/** How far apart two hexes of the wall's ring are, along it. */
const ringGap = (a: number, b: number): number => imin((a - b + 24) % 24, (b - a + 24) % 24);

/** One try at a layout from this stream (null: it didn't make a fair puzzle). */
const attempt = (rng: RngState): WedgeLayout | null => {
  const turn = rngInt(rng, 6);
  const flip = rngInt(rng, 2) === 1;
  const T = (h: Hex): Hex => rot(mirror(h, flip), turn);
  // Drawn facing one way (the column from the top, the cliff on the left), then turned and mirrored.
  const rim = hexRing(WEDGE_RADIUS);
  const ring4 = hexRing(WALL);
  const ring5 = hexRing(WALL + 1);
  // The column comes in near the top of the rim.
  const top = rim.reduce((a, b) => (hexDist(b, { q: 3, r: -7 }) < hexDist(a, { q: 3, r: -7 }) ? b : a));
  const ti = rim.findIndex((h) => h.q === top.q && h.r === top.r);
  const entry = rim[(ti + rngInt(rng, 3) - 1 + rim.length) % rim.length] as Hex;
  // The gap the ledge comes down to: one of the wall's hexes a little to the left of the way in.
  const byNear = ring4.map((h, i) => ({ h, i, d: hexDist(h, entry) })).sort((a, b) => a.d - b.d || a.i - b.i);
  const nearI = (byNear[0] as { i: number }).i;
  const off = 3 + rngInt(rng, 3);
  // Left is the way the ring runs here, or back: the mirror covers the other side.
  const fi = (nearI - off + ring4.length) % ring4.length;
  const f1 = ring4[fi] as Hex;
  // The ledge runs along the next ring out, away from the column, from the gap's side.
  const at5 = ring5.map((h, i) => ({ h, i })).filter((x) => hexDist(x.h, f1) === 1);
  if (!at5.length) return null;
  // Of the outer hexes by the gap, the one nearer the column starts the ledge, and it runs toward the column: its
  // mouth opens to the way in, a short way round the wall's front.
  const start = at5.sort((a, b) => hexDist(a.h, entry) - hexDist(b.h, entry) || a.i - b.i)[0] as { h: Hex; i: number };
  const n5 = ring5.length;
  const nextAway = hexDist(ring5[(start.i + 1) % n5] as Hex, entry) <= hexDist(ring5[(start.i - 1 + n5) % n5] as Hex, entry) ? 1 : -1;
  const at = (k: number): Hex => ring5[(start.i + k * nextAway + 2 * n5) % n5] as Hex;
  const strip = [at(0), at(1), at(2)];
  const mouth = at(3);
  const shut5 = at(-1);
  const rock = new Set<string>();
  const wall: Hex[] = [];
  const cliff: Hex[] = [];
  const crags: Hex[] = [];
  const stripK = new Set(strip.map(key));
  const keepOpen = new Set<string>([...stripK, key(mouth)]);
  // The loose rock on the wall's stone beside the strip's middle (never the ledge's gap), pushed from inside the yard.
  const mid = strip[1] as Hex;
  const sill = ring4.find((h) => hexDist(h, mid) === 1 && hexDist(h, f1) > 0 && hexDist(h, f1) >= 1 && !(h.q === f1.q && h.r === f1.r));
  const nook = sill ? nbrs(sill).find((x) => hexLen(x.q, x.r) === WALL - 1 && hexDist(x, f1) >= 2) : undefined;
  if (!sill || !nook) return null;
  // The cliff: the ring out from the strip and its near end, two deep, and the near end of the ledge.
  const cliffSeed = [shut5, ...strip, ...nbrs(shut5).filter((x) => hexLen(x.q, x.r) >= WALL + 1)];
  for (const s of cliffSeed)
    for (const x of [s, ...nbrs(s)]) {
      const d = hexLen(x.q, x.r);
      if (d <= WALL || d > WEDGE_RADIUS || keepOpen.has(key(x)) || rock.has(key(x))) continue;
      if (hexDist(x, mouth) <= 1 && !nbrs(x).some((y) => stripK.has(key(y)))) continue;
      rock.add(key(x));
      cliff.push(x);
    }
  // A second band of cliff behind the first, so it reads as the mountain's face (never over the mouth's way out).
  for (const c of cliff.slice())
    for (const x of nbrs(c)) {
      const d = hexLen(x.q, x.r);
      if (d <= WALL + 1 || d > WEDGE_RADIUS || keepOpen.has(key(x)) || rock.has(key(x)) || hexDist(x, mouth) <= 2 || hexDist(x, entry) <= 2) continue;
      rock.add(key(x));
      cliff.push(x);
    }
  // The gaps: the ledge's, and five more along the wall, two hexes apart at least, none by the ledge.
  const byLedge = new Set(ring4.filter((h) => [...strip, mouth, shut5].some((s) => hexDist(s, h) === 1) || hexDist(h, nook) <= 1).map(key));
  const cand = ring4.map((h, i) => ({ h, i })).filter((x) => x.i !== fi && !byLedge.has(key(x.h)) && ringGap(x.i, fi) >= 2);
  const picked: number[] = [fi];
  let guard = 0;
  while (picked.length < WEDGE_GAPS && guard++ < 60) {
    const c = cand[rngInt(rng, cand.length)] as { h: Hex; i: number };
    if (picked.some((p) => ringGap(p, c.i) < 2)) continue;
    picked.push(c.i);
  }
  if (picked.length < WEDGE_GAPS) return null;
  picked.sort((a, b) => a - b);
  const gapsC = picked.map((i) => ring4[i] as Hex);
  const gapK = new Set(gapsC.map(key));
  for (const h of ring4)
    if (!gapK.has(key(h))) {
      rock.add(key(h));
      wall.push(h);
    }
  // A few outcrops out in the open, never on the way in, the mouth or by the wall.
  const crag = rngInt(rng, 4);
  for (let k = 0; k < crag; k++) {
    const h = rim[rngInt(rng, rim.length)] as Hex;
    const x = add(h, DIRS[rngInt(rng, 6)] as Hex);
    for (const y of [x, add(x, DIRS[rngInt(rng, 6)] as Hex)]) {
      const d = hexLen(y.q, y.r);
      if (d <= WALL + 1 || d > WEDGE_RADIUS || hexDist(y, entry) <= 2 || hexDist(y, mouth) <= 2 || rock.has(key(y)) || keepOpen.has(key(y))) continue;
      rock.add(key(y));
      crags.push(y);
    }
  }
  const sorted = (hs: Hex[]): Hex[] => hs.map(T).sort((a, b) => a.r - b.r || a.q - b.q);
  // Turn everything; the gaps keep their order along the wall.
  const L: WedgeLayout = {
    entry: T(entry),
    rocks: sorted([...wall, ...cliff, ...crags]),
    wall: sorted(wall),
    cliff: sorted(cliff),
    crags: sorted(crags),
    gaps: gapsC.map(T),
    strip: [at(2), at(1), at(0)].map(T),
    rock: T(sill),
    nook: T(nook),
    solution: [],
    front: [],
  };
  // The check: one planting of the twenty under the cornice, none of the others near it.
  let good: number[] | null = null;
  for (const p of WEDGE_PLANTINGS) {
    const w = wedgeWay(L, p);
    if (w === null || w === 'mixed') return null;
    if (w === 'under') {
      if (good) return null;
      good = p;
    }
  }
  if (!good) return null;
  // With nothing planted the way doesn't go under it either.
  if (wedgeWay(L, []) !== 'clear') return null;
  const front = L.gaps
    .map((h, i) => ({ i, d: hexDist(h, L.entry) }))
    .sort((a, b) => a.d - b.d || a.i - b.i)
    .slice(0, WEDGE_SHIELDS)
    .map((x) => x.i)
    .sort((a, b) => a - b);
  // The trap: the obvious three hold the ledge's gap (so the way goes round by a side gap).
  if (!front.includes(picked.indexOf(fi))) return null;
  L.solution = good;
  L.front = front;
  return L;
};

/** Tries a seed gets before the fallback (one in a hundred or so makes a fair ledge: a thousand seeds never miss). */
const TRIES = 4000;
/** A seed known to make a fair ledge (a test holds it to that): the last resort, never a crash. */
export const WEDGE_FALLBACK = 13;

/** The ledge of a fight's seed: the first fair layout of its tries. */
export const wedgeLayout = (seed: number): WedgeLayout => {
  for (const sd of [seed, WEDGE_FALLBACK]) {
    for (let k = 0; k < TRIES; k++) {
      const L = attempt(seedRng(deriveSeed(sd, 0x3d6e + k)));
      if (L) return L;
    }
  }
  throw new Error('wedgeLayout: no fair ledge');
};

/* ---------- the wedges, large (chapter 4) ---------- */

/**
 * The wedges, large (docs/sendas/guerrero.md §4, «Las cuñas», chapter 4): the avalanche's shadow on the slope, split
 * into seven lanes, and nine rocks in three rows (three a row), each with its grain leaning to a side. The snow comes
 * straight down its lane with a thickness (one in each at the top); a wedge in a rock turns that rock's whole lane into
 * the next one the way the grain leans, added to what comes down there; a wedge holds up to two (three or more and the
 * branch takes it, and goes on straight); what goes off the slope's sides falls into the void. The rows go from the top
 * down, and a row's wedges turn at once what came to that row. Won with nothing coming down lanes 3, 4 and 5 (the cave's
 * mouth): a clean V. The slopes are a table in content (`AVALANCHE_SLOPES`), each with one answer only, and all four
 * wedges wanted; `laneSolve` is the check. Lanes here count from 0 (the content's 1 to 7).
 */

export const LANES = 7;
/** The lanes over the cave's mouth (the content's 3, 4 and 5). */
export const MOUTH_LANES = [2, 3, 4] as const;
/** A wedge holds this much snow; more breaks through. */
export const WEDGE_HOLDS = 2;

/** A rock of the large wedges: its lane (0 to 6) and its grain (+1 toward the higher lane, −1 the lower). */
export interface LaneRock {
  lane: number;
  dir: number;
}

/** A slope from the content's table (`3>5>7<|…`): three rows of three rocks, from the top. */
export const parseSlope = (s: string, mirror = false): LaneRock[][] =>
  s.split('|').map((row) => {
    const out: LaneRock[] = [];
    for (let i = 0; i + 1 < row.length; i += 2) {
      const lane = (row.charCodeAt(i) - 49) | 0;
      const dir = row[i + 1] === '>' ? 1 : -1;
      out.push(mirror ? { lane: LANES - 1 - lane, dir: -dir } : { lane, dir });
    }
    return out.sort((a, b) => a.lane - b.lane);
  });

/**
 * How the snow comes down with these wedges in (`shut[i]` 1: the rock `i`, counted row by row, has one): the
 * thickness in each lane above the first row and below each row (`seg[0]` to `seg[3]`), and which wedges the snow
 * broke through.
 */
export const laneFlow = (rows: LaneRock[][], shut: ArrayLike<number>): { seg: number[][]; broke: boolean[] } => {
  let g = new Array<number>(LANES).fill(1);
  const seg: number[][] = [g.slice()];
  const broke: boolean[] = [];
  let i = 0;
  for (const row of rows) {
    const out = g.slice();
    for (const rk of row) {
      const k = i++;
      broke[k] = false;
      if (shut[k] !== 1) continue;
      const n = g[rk.lane] as number;
      if (n === 0) continue;
      if (n > WEDGE_HOLDS) {
        broke[k] = true;
        continue;
      }
      out[rk.lane] = (out[rk.lane] as number) - n;
      const to = rk.lane + rk.dir;
      if (to >= 0 && to < LANES) out[to] = (out[to] as number) + n;
    }
    g = out;
    seg.push(g.slice());
  }
  return { seg, broke };
};

/** Whether the snow at the foot misses the cave's mouth. */
export const laneClear = (foot: ArrayLike<number>): boolean => MOUTH_LANES.every((l) => foot[l] === 0);

/**
 * The check (the table was drawn with it): the one planting of four wedges or fewer that keeps the mouth clear, or
 * null when there is none, more than one, or one with fewer than four.
 */
export const laneSolve = (rows: LaneRock[][]): number[] | null => {
  const n = rows.reduce((a, r) => a + r.length, 0);
  let found: number[] | null = null;
  const shut = new Array<number>(n).fill(0);
  for (let m = 0; m < 1 << n; m++) {
    let c = 0;
    for (let i = 0; i < n; i++) c += (m >> i) & 1;
    if (c > 4) continue;
    for (let i = 0; i < n; i++) shut[i] = (m >> i) & 1;
    const f = laneFlow(rows, shut);
    if (!laneClear(f.seg[f.seg.length - 1] as number[])) continue;
    if (found) return null;
    found = [];
    for (let i = 0; i < n; i++) if ((m >> i) & 1) found.push(i);
  }
  return found && found.length === 4 ? found : null;
};

/** A fight's slope: one of the table's by its seed, mirrored or not, and its answer (rock indices, row by row). */
export const bigWedges = (seed: number, table: readonly string[]): { rows: LaneRock[][]; solution: number[]; index: number; mirror: boolean } => {
  const rng = seedRng(deriveSeed(seed, 0xa1a7));
  const index = rngInt(rng, table.length);
  const mirror = rngInt(rng, 2) === 1;
  const rows = parseSlope(table[index] as string, mirror);
  return { rows, solution: laneSolve(rows) ?? [], index, mirror };
};

/**
 * The obvious planting (docs/sendas/guerrero.md §4: «empezar por el medio»): in each row the rock nearest the middle
 * lane, down the slope, and the fourth wedge in the low row's next nearest (over the mouth). No slope of the table
 * clears with it (a test holds it): it gathers the snow in the middle and a wedge breaks.
 */
export const laneObvious = (rows: LaneRock[][]): number[] => {
  const out: number[] = [];
  const near = (row: LaneRock[], base: number): void => {
    let best = -1;
    let bd = 99;
    row.forEach((k, j) => {
      const d = Math.abs(k.lane - 3);
      if (!out.includes(base + j) && d < bd) {
        bd = d;
        best = base + j;
      }
    });
    if (best >= 0) out.push(best);
  };
  let base = 0;
  for (const row of rows) {
    near(row, base);
    base += row.length;
  }
  const low = rows[rows.length - 1];
  if (low) near(low, base - low.length);
  return out.sort((a, b) => a - b);
};
